import { useSelector } from 'react-redux';
import {
  useGetAllAvailableBalancesFromExchangesQuery,
  useGetTopBotsQuery,
  useGetUserAvailableCreditsQuery,
} from '../../store/services/api';
import { selectUserId } from '../../store/slices/user';
import React, { useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { GetTopAssetsByVolumeInfo } from '../../types/Bot';
import { selectDisplayBots } from '../../store/slices/bot';
import { Tabs } from './types';
import SearchCoin from './SearchCoin';
import PredefinedBaskets from './PredefinedBaskets';
import CopyMeBaskets from './CopyMeBaskets';

const Discover = () => {
  const [activeTab, setActiveTab] = useState(Tabs.SearchCoin);

  return (
    <Layout>
      <Layout.SideMenu>
        <Layout.SideMenuSection>
          <Layout.SideMenuTitle title="Actions" />
          {Object.values(Tabs).map((action) => (
            <Layout.SideMenuItem
              key={action}
              id={action}
              label={action}
              onClick={setActiveTab}
              isActive={activeTab === action}
            />
          ))}
        </Layout.SideMenuSection>
      </Layout.SideMenu>

      <Layout.Content>
        <Content activeTab={activeTab} />
      </Layout.Content>
    </Layout>
  );
};

interface ContentProps {
  activeTab: Tabs;
}

const Content = ({ activeTab }: ContentProps) => {
  const userId = useSelector(selectUserId);

  const {
    data: creditsData,
    isLoading: isCreditsLoading,
    refetch: refetchCredits,
  } = useGetUserAvailableCreditsQuery(userId);
  const {
    data: balancesData,
    isLoading: isBalancesLoading,
    refetch: refetchBalances,
  } = useGetAllAvailableBalancesFromExchangesQuery(userId);
  const balances = balancesData?.balances;
  const credits = creditsData?.credits;

  const availableStableFunds = useMemo(() => {
    // return { binance: 54, kucoin: 14934, mexc: 59482203 };
    if (!balances) {
      return {};
    }

    // Record<exchange, Record<quoteAsset, number>
    const funds = Object.entries(balances).reduce<
      Record<string, Record<string, number>>
    >((acc, [exchange, balancesByQuoteAsset]) => {
      if (balancesByQuoteAsset.USDT) {
        if (!acc[exchange]) {
          acc[exchange] = {};
        }

        acc[exchange].USDT = balancesByQuoteAsset.USDT;
      }

      if (balancesByQuoteAsset.USDC) {
        if (!acc[exchange]) {
          acc[exchange] = {};
        }

        acc[exchange].USDC = balancesByQuoteAsset.USDC;
      }

      return acc;
    }, {});

    return funds;
  }, [balances]);

  const refresh = () => {
    refetchCredits();
    refetchBalances();
  };

  return (
    <>
      {activeTab === Tabs.SearchCoin && (
        <SearchCoin
          availableStableFunds={availableStableFunds}
          balances={balances}
          credits={credits}
          isLoading={isCreditsLoading || isBalancesLoading}
          refresh={refresh}
        />
      )}
      {activeTab === Tabs.PredefinedBaskets && (
        <PredefinedBaskets
          credits={credits}
          balances={balances}
          availableStableFunds={availableStableFunds}
          refresh={refresh}
        />
      )}
      {activeTab === Tabs.CopyMeBaskets && (
        <CopyMeBaskets
          credits={credits}
          balances={balances}
          availableStableFunds={availableStableFunds}
          refresh={refresh}
        />
      )}
    </>
  );
};

export default Discover;
